import moment from "moment";
import { nextTick } from "vue";
import type { RouteLocationNamedRaw } from "vue-router";

import { removeFiltersThatDoNotPassForTask } from "~/components/filters/utils";
import { LayoutKind, SkillKind } from "~/shared/enums";
import type { Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { getTaskLink } from "~/utils/common";
import { formatDate } from "~/utils/date";
import { when } from "~/utils/wait";

import type { Actions } from "..";

/** Command related actions */
export default (actions: Actions) => ({
  jumpToDescription: async (task?: Task) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();

    let targetTask = task;
    if (!targetTask) {
      const currents = actions.visualization.getCurrentRows(true);
      if (currents.length !== 1 || !currents[0].data) {
        return;
      }
      targetTask = await dataStore.getTaskByDuid(currents[0].data.duid);
      if (!targetTask) {
        return;
      }
    }

    if (!appStore.taskOpenInDetail) {
      appStore.setTaskDetailOpen(true);
    }
    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(() => {
      appStore.taskDetail?.jumpToDescription();
    }, 100);
  },
  jumpToComments: async (task?: Task) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();

    let targetTask = task;
    if (!targetTask) {
      const currents = actions.visualization.getCurrentRows(true);
      if (currents.length !== 1 || !currents[0].data) {
        return;
      }
      targetTask = await dataStore.getTaskByDuid(currents[0].data.duid);
      if (!targetTask) {
        return;
      }
    }

    if (!appStore.taskOpenInDetail) {
      appStore.setTaskDetailOpen(true);
    }
    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(() => {
      appStore.taskDetail?.jumpToComments();
    }, 100);
  },
  navigateToTask: async (taskDuid: string, options: { maintainQuery?: boolean; skipNavigate?: boolean } = {}) => {
    const { maintainQuery = false, skipNavigate = false } = options;
    const appStore = useAppStore();
    const dataStore = useDataStore();

    const task = await dataStore.getTaskByDuid(taskDuid);
    if (!task) {
      appStore.setTaskDetailOpen(false);
      appStore.setTaskDetailLoading(false);
      return;
    }

    if (!skipNavigate) {
      const newRoute: RouteLocationNamedRaw = getTaskLink(task);
      if (maintainQuery) {
        newRoute.query = appStore.$router.currentRoute.value.query;
      }
      await appStore.$router.push(newRoute);
    }

    removeFiltersThatDoNotPassForTask(dataStore, task);
    await nextTick();

    const visualization = appStore.getBaseVisualization();
    const row = visualization.getRow(task.duid);
    if (!row) {
      await visualization.hardDisplayTask(task);
    }
    await when(() => !!visualization.getRow(task.duid));

    visualization.selectAndScrollTo(task.duid);
    appStore.setTaskDetailOpen(true);
    appStore.setTaskDetailLoading(false);
  },
  createTaskQuickly: () => {
    const appStore = useAppStore();

    // TODO handle more cases here, add it to visualization
    if (appStore.layoutKind === LayoutKind.BOARD) {
      appStore.board?.createTask();
    } else if (appStore.newTask) {
      appStore.newTask.focus();
    }
  },
  contextualCreate: () => {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const tenantStore = useTenantStore();

    if (appStore.spaceOrFolder) {
      appStore.spaceOrFolder.createDoc();
      return;
    }

    if (appStore.viewsRoot) {
      appStore.viewsRoot.createView();
      return;
    }

    if (appStore.dashboardsRoot) {
      appStore.dashboardsRoot.createDashboard();
      return;
    }

    actions.visualization.openKeyboardIfIos();
    appStore.setTcmOpen(true);

    if (appStore.layoutKind !== LayoutKind.CALENDAR) {
      return;
    }

    nextTick(() => {
      if (!dataStore.taskDraft) {
        return;
      }
      const date = formatDate(moment());
      const updates = [
        { duid: dataStore.taskDraft.duid, startAt: tenantStore.startDateEnabled ? date : undefined, dueAt: date },
      ];
      dataStore.updateTasks(updates, { noUndo: true, noBackend: true });
      // oxlint-disable-next-line dart/no-set-timeout
      setTimeout(() => {
        dataStore.updateTasks(updates, { noUndo: true });
      }, 1000);
    });
  },
  startEditingTitle: () => {
    const appStore = useAppStore();
    const visualization = appStore.getActiveVisualization();

    const currents = actions.visualization.getCurrentRows(true);
    if (actions.visualization.currentRowIsCreateModal(currents)) {
      return;
    } // TODO should actually select the title tho
    if (currents.length !== 1 || !currents[0].data) {
      return;
    }

    visualization.editTitle(currents[0].data.duid);
  },
  forceDeleteTask: (task: Task) => {
    const dataStore = useDataStore();

    dataStore.deleteTasks([task]);
  },
  deleteTask: async () => {
    const appStore = useAppStore();
    const route = appStore.$router.currentRoute.value;

    if (route.name !== "trash") {
      await actions.visualization.trashTasks();
      return;
    }
    await actions.visualization.deleteTasks();
  },
  openKeyboardIfIos: () => {
    const pageStore = usePageStore();
    if (!pageStore.isIos) {
      return;
    }

    const tempInput = document.createElement("input");
    tempInput.style.position = "absolute";
    tempInput.style.opacity = "0";
    tempInput.style.fontSize = "16px"; // iOS wont zoom in with font size >= 16px
    tempInput.readOnly = true;

    document.body.prepend(tempInput);
    tempInput.focus();

    tempInput.addEventListener("blur", () => {
      tempInput.remove();
    });
  },
  startBrainstorm: async () => {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const userStore = useUserStore();

    userStore.setAiChatPanelOpen(true);
    await nextTick();

    const brainstormSkill = dataStore
      .skillList({ includeHidden: true })
      .find((skill) => skill.kind === SkillKind.BRAINSTORM_NEW_TASKS);
    if (!brainstormSkill) {
      return;
    }

    appStore.chatPanel?.sendContentInNewChat(brainstormSkill);
  },
});
